<template>
	<div class="ReportMeta u-stack-inside-responsive">
		<!-- Hidden heading -->
		<CloHeading class="is-visuallyHidden" level="2">{{ $t('tripReport.details') }}</CloHeading>
		<!-- Regions (hidden for medium up) -->
		<section v-if="Object.keys(tripReportModelData.regions).length > 0" :class="isPrintLayout ? '' : 'u-hideForMedium'">
			<CloHeading class="is-visuallyHidden" level="3">{{ $t('generic.Regions') }}</CloHeading>
			<ReportMetaRegions :regions="tripReportModelData.regions" :compact="true" />
		</section>
		<!-- People (hidden for medium up) -->
		<section :class="isPrintLayout ? '' : 'u-hideForMedium'">
			<CloHeading class="is-visuallyHidden" level="3">{{ $t('tripReport.people') }}</CloHeading>
			<ReportMetaPeople :confirmedPeople="confirmedPeople" />
		</section>

		<!-- Narrative -->
		<section v-if="narrative" class="ReportMeta-narrative u-stack-inside-sm">
			<CloHeading class="is-visuallyHidden" level="3">{{ $t('tripReport.narrative') }}</CloHeading>
			<p
				class="ReportMeta-narrative-content"
				v-html="narrative"
				v-bind:class="{ clamped: !expandedNarrative }"
				v-on:click="expandedNarrative = true"
			></p>
			<div v-if="!isPrintLayout && (currentUser.role === 'owner' || currentUser.role === 'editor')">
				<a
					:href="`${baseUrl}tripreport/edit/${tripReportModelData.tripReportId}?view=narrative`"
					class="Button Button--hollow Button--small"
					>{{ $t('tripReport.narrativeEdit') }}</a
				>
			</div>
		</section>
		<section v-else class="ReportMeta-narrative u-showForMedium">
			<!-- Empty narrative owner/editor teaser -->
			<div class="ReportMeta-empty">
				<CloHeading level="3" size="4">{{ $t('tripReport.narrative') }}</CloHeading>
				<p>{{ $t('tripReport.narrativeAbout') }}</p>
				<a
					v-if="currentUser.role === 'owner' || currentUser.role === 'editor'"
					:href="`${baseUrl}tripreport/edit/${tripReportModelData.tripReportId}?view=narrative`"
					class="Button Button--hollow Button--small"
					>{{ $t('tripReport.narrativeAdd') }}</a
				>
			</div>
		</section>

		<!-- Comments -->
		<section v-if="comments.length > 0" class="ReportMeta-comments u-stack-inside-sm">
			<CloHeading class="is-visuallyHidden" level="3">{{ $t('tripReport.comments') }}</CloHeading>
			<div class="ReportMeta-comments-summary">
				<button class="u-inline-inside-xs" v-on:click="expandedComments = !expandedComments">
					<CloIcon v-show="!expandedComments" symbol="triangleRight" size="sm" />
					<CloIcon v-show="expandedComments" symbol="triangleDown" size="sm" />
					<CloIcon symbol="comment" size="sm" />
					<span class="is-visuallyHidden">{{ $t('data.total') }}: </span>
					<span>{{ comments.length }}</span>
				</button>
			</div>
			<ul id="report-comments" v-show="expandedComments" class="u-unlist u-stack-inside-responsive">
				<li v-for="comment in comments" :key="comment.tripReportPersonId" class="ReportMeta-comments-comment">
					<div class="ReportMeta-comments-comment-heading">
						<CloHeading level="4" size="5">
							<Person :person="getPerson(comment.tripReportPersonId)" />
						</CloHeading>
						<a
							v-if="comment.tripReportPersonId === currentUser.tripReportPersonId"
							:href="`${baseUrl}tripreport/edit/${tripReportModelData.tripReportId}?view=comment`"
							class="Button Button--link Button--small"
							>{{ $t('tripReport.commentEdit') }}</a
						>
					</div>
					<div v-html="comment['comment']"></div>
				</li>
			</ul>
			<div v-if="currentUser.role == 'participant' && !hasCurrentUserComment">
				<a
					:href="`${baseUrl}tripreport/edit/${tripReportModelData.tripReportId}?view=comment`"
					class="Button Button--hollow Button--small"
					>{{ $t('tripReport.commentAdd') }}</a
				>
			</div>
		</section>
		<!-- Empty comments participant teaser -->
		<section v-else class="ReportMeta-comments u-showForMedium">
			<div v-if="currentUser.role == 'participant'" class="ReportMeta-empty">
				<CloHeading level="3" size="4">{{ $t('tripReport.comments') }}</CloHeading>
				<p>{{ $t('tripReport.commentAbout') }}</p>
				<a
					:href="`${baseUrl}tripreport/edit/${tripReportModelData.tripReportId}?view=comment`"
					class="Button Button--hollow Button--small"
					>{{ $t('tripReport.commentAdd') }}</a
				>
			</div>
		</section>
	</div>
</template>

<script>
import axios from 'axios';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import ReportMetaPeople from './ReportMetaPeople.vue';
import ReportMetaRegions from './ReportMetaRegions.vue';
import Person from './Person.vue';
export default {
	name: 'ReportMeta',
	components: {
		CloIcon,
		CloButton,
		CloHeading,
		ReportMetaPeople,
		ReportMetaRegions,
		Person,
	},
	props: {
		tripReportModelData: Object,
		confirmedPeople: Array,
		currentUser: Object,
		isPrintLayout: Boolean,
		comments: {
			type: Array,
			required: true,
		},
		narrative: String,
	},
	data() {
		return {
			expandedNarrative: false,
			expandedComments: false,
			tripReportId: this.tripReportModelData.tripReportId,
			baseUrl: window.baseUrl,
			tripApiBaseUrl: window.tripApiBaseUrl,
		};
	},
	mounted() {
	},
	methods: {
		getPerson(personId) {
			let person = this.tripReportModelData.people.filter(function (e) {
				return e.tripReportPersonId == personId;
			});
			if (person.length > 0) return person[0];
		},
	},
	computed: {
		hasCurrentUserComment() {
			if (this.comments.length > 0) {
				return this.comments.find((comment) => comment.tripReportPersonId === this.currentUser.tripReportPersonId);
			}
			return false;
		},
	},
};
</script>

<style lang="scss">
.ReportMeta {
	@include text(3);
	background-color: $color-primary-0;
	@include inset-responsive;
}
.ReportMeta-narrative,
.ReportMeta-comments {
	@include text(4, loose);
	.Button {
		margin: 0;
	}
}
.ReportMeta-narrative-content {
	--narrativeLineClamp: 3;
	@include breakpoint(medium) {
		--narrativeLineClamp: 9;
	}
	margin: 0;
	&.clamped {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-line-clamp: var(--narrativeLineClamp, 3);
		-webkit-box-orient: vertical;
		overflow-y: hidden;
		@include breakpoint(medium) {
			-webkit-line-clamp: unset; // remove line clamping for now
		}
	}
}
.ReportMeta-comments-summary {
	display: flex;
	align-items: center;
	button {
		display: inline-flex;
		align-items: center;
		@include text(3, loose);
		color: $color-primary-3;
	}
}
.ReportMeta-comments-comment {
	background-color: $color-white;
	@include inset-md;
	border-radius: 3px;
	.ReportMeta-comments-comment-heading {
		display: flex;
		align-items: baseline;
		gap: $space-sm;
		.Heading {
			flex-grow: 1;
		}
	}
}

.ReportMeta-empty {
	@include inset-md;
	background-color: $color-white;
	border-radius: $radius-sm;
	.Heading {
		margin: 0;
	}
	p {
		@include stack-xs;
		@include text(3, loose);
		color: $color-text-muted;
	}
	.Button {
		margin: 0;
	}
}

.print {
	.ReportMeta {
		background-color: transparent;
		padding: 0;
		@include stack-md;
		gap: $space-sm;
	}
	.ReportMeta-narrative-content.clamped {
		-webkit-line-clamp: none;
	}
	.ReportMeta-comments,
	.ReportMeta-empty,
	.Button {
		display: none;
	}
}
</style>
